import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Analytics from "./pages/Analytics";
import CounselorReports from "./pages/CounselorReports";
import Leads from "./pages/Leads";
import Applications from "./pages/Applications";
import Integrations from "./pages/Integrations";
import CampaignUsage from "./pages/CampaignUsage";
import Payments from "./pages/Payments";
import AssignmentRules from "./pages/AssignmentRules";
import InstitutionSettings from "./pages/InstitutionSettings";
import StaffManagement from "./pages/StaffManagement";
import AuditTrail from "./pages/AuditTrail";
import PlatformTenants from "./pages/PlatformTenants";
import Apply from "./pages/Apply";
import CandidateReupload from "./pages/CandidateReupload";
import ApplicationTracker from "./pages/ApplicationTracker";
import CounselorTasks from "./pages/CounselorTasks";
import TenantSuspended from "./pages/TenantSuspended";
import TenantPaused from "./pages/TenantPaused";
import { IS_STANDALONE } from "./lib/deployment";

function Protected({ children, roles, platform, redirectTo }) {
  const { user, loading, viewRole } = useAuth();
  if (platform && IS_STANDALONE) return <Navigate to="/analytics" replace />;
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]" data-testid="auth-loading">
        <div className="w-8 h-8 border-2 border-slate-300 border-t-slate-800 rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (platform && !user.is_platform_admin) {
    // Same redirectTo escape hatch the roles branch below uses — a tenant
    // super_admin hitting /platform/tenants directly by URL gets bounced
    // straight to redirectTo rather than shown an in-layout notice, per
    // the "get bounced immediately" ask. user.is_platform_admin is the
    // real field (this app has no `role: "platform_owner"` value — a
    // user's `role` is their tenant-level role, orthogonal to this flag;
    // see routes_auth.require_platform_owner's docstring).
    if (redirectTo) return <Navigate to={redirectTo} replace />;
    return <Layout><div className="py-32 text-center" data-testid="no-access-message">
      <h2 className="font-display text-2xl font-bold text-slate-800">Platform admin only</h2></div></Layout>;
  }
  if (roles && !roles.includes(viewRole)) {
    // Most restricted routes just show an in-layout "no access" notice —
    // redirectTo is an opt-in exception for a route a specific role
    // shouldn't land on at all (e.g. a counselor hitting /analytics),
    // rather than a blanket behavior change for every roles-gated route.
    if (redirectTo) return <Navigate to={redirectTo} replace />;
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center py-32" data-testid="no-access-message">
          <h2 className="font-display text-2xl font-bold text-slate-800">No access for this role</h2>
          <p className="text-sm text-slate-500 mt-2">Switch roles from the top-right RBAC switcher to view this module.</p>
        </div>
      </Layout>
    );
  }
  return <Layout>{children}</Layout>;
}

// On a tenant subdomain (zenith.wizardsoftware.in, aurora.wizardsoftware.in, …)
// the root path IS that college's application portal — resolved by Host, no slug.
function isTenantHost() {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname;
  return /\.wizardsoftware\.in$/i.test(h) && !/^(crm|www)\./i.test(h);
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/" element={isTenantHost() ? <Apply /> : <Navigate to="/analytics" replace />} />
          <Route path="/apply" element={<Apply />} />
          <Route path="/apply/:slug" element={<Apply />} />
          <Route path="/reupload" element={<CandidateReupload />} />
          <Route path="/track" element={<ApplicationTracker />} />
          <Route path="/track/:token" element={<ApplicationTracker />} />
          <Route path="/tenant-suspended" element={<TenantSuspended />} />
          <Route path="/tenant-paused" element={<TenantPaused />} />
          <Route path="/analytics" element={<Protected roles={["super_admin", "dean", "publisher", "finance"]} redirectTo="/applications"><Analytics /></Protected>} />
          <Route path="/analytics/counselors" element={<Protected roles={["super_admin", "dean"]}><CounselorReports /></Protected>} />
          <Route path="/leads" element={<Protected roles={["super_admin", "dean", "counselor"]}><Leads /></Protected>} />
          <Route path="/tasks" element={<Protected roles={["super_admin", "dean", "counselor"]}><CounselorTasks /></Protected>} />
          <Route path="/applications" element={<Protected roles={["super_admin", "dean", "counselor"]}><Applications /></Protected>} />
          <Route path="/integrations" element={<Protected roles={["super_admin", "publisher", "dean"]}><Integrations /></Protected>} />
          <Route path="/campaigns/usage" element={<Protected roles={["super_admin", "dean"]}><CampaignUsage /></Protected>} />
          <Route path="/payments" element={<Protected roles={["super_admin", "finance", "dean"]}><Payments /></Protected>} />
          <Route path="/assignment-rules" element={<Protected roles={["super_admin", "dean"]}><AssignmentRules /></Protected>} />
          <Route path="/institution-settings" element={<Protected roles={["super_admin", "dean"]}><InstitutionSettings /></Protected>} />
          <Route path="/staff" element={<Protected roles={["super_admin", "dean"]}><StaffManagement /></Protected>} />
          <Route path="/audit-trail" element={<Protected roles={["super_admin", "dean"]}><AuditTrail /></Protected>} />
          <Route path="/platform/tenants" element={<Protected platform redirectTo="/applications"><PlatformTenants /></Protected>} />
          <Route path="*" element={<Navigate to="/analytics" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
